import type { Annotation } from "building-types/annotation/annotationTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { ContentTemplateHandle } from "building-types/handles/docEntityHandleTypes";
import { type ContentTemplate, type SheetTemplateInstance } from "building-types/views/planSets";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import objectFind from "common/utils/objectFind";
import { isAnnotationDrawingTemplate, isTagTemplate } from "../../contentTemplateTools";
import type { DocEntityNode } from "../docEntityNode";
import { DocEntityNodeBase, type DocEntityNodeSource } from "../docEntityNodeBase";

// Annotations within tag templates are not directly hosted by the model, but are rendered in model space
// by tagged entities like rooms. If a tag template is in use by a view template or model space entity,
// changes to annotations could affect viewports and/or the model editor view.
function isActiveTagTemplate(doc: Doc, parentTemplate: ContentTemplate) {
  if (!isTagTemplate(parentTemplate)) {
    return false;
  }

  return (
    !!objectFind(
      doc.viewTemplates,
      (viewTemplate) => viewTemplate.viewType === "PLAN_VIEW" && viewTemplate.roomTagTemplateId === parentTemplate.id,
    ) || !!objectFind(doc.rooms, (room) => room.roomTagTemplateId === parentTemplate.id)
  );
}

/**
 * Check if the entity has any instances that are hosted in model space.
 * Conservative check, returns true if unsure.
 */
export function docAnnotationOrDrawingIsModelSpaceHosted(
  doc: Doc,
  parentId: DocEntityParentId | undefined,
  docEntityNodeSource: DocEntityNodeSource,
): boolean {
  // We don't operate on nodes here unless we have to so that we avoid extra node lookups.
  // This function runs in an inner loop so it's important to keep it fast.
  if (parentId === undefined) {
    return true;
  }
  if (doc.contentTemplateInstances[parentId] !== undefined) {
    // Model hosted drawing _instances_ can't have child annotations, the children are on the template.
    return false;
  }
  const parentTemplate = doc.contentTemplates[parentId];
  if (!parentTemplate) {
    return true;
  }

  if (isActiveTagTemplate(doc, parentTemplate)) {
    return true;
  }

  if (!isAnnotationDrawingTemplate(parentTemplate)) {
    return false;
  }
  // If any of the instances of the drawing template are hosted in model space, then the children of the template are hosted in model space.
  const templateNode = docEntityNodeSource.getNode(doc, { type: "CONTENT_TEMPLATE", id: parentTemplate.id });
  assertIsContentTemplateDocEntityNode(templateNode);
  return templateNode.hasModelHostedInstances();
}

export function docContentTemplateInstanceGetHostSheets(
  doc: Doc,
  contentTemplateInstanceId: DocEntityParentId | undefined,
  docEntityNodeSource: DocEntityNodeSource,
  visitedInstanceIds: Set<string> = new Set(),
): SheetTemplateInstance[] {
  if (contentTemplateInstanceId === undefined) {
    return [];
  }

  if (visitedInstanceIds.has(contentTemplateInstanceId)) {
    return [];
  }

  const nextVisitedInstanceIds = new Set(visitedInstanceIds);
  nextVisitedInstanceIds.add(contentTemplateInstanceId);

  const contentTemplateInstance = doc.contentTemplateInstances[contentTemplateInstanceId];

  if (!contentTemplateInstance) {
    return [];
  }

  // This is a sheet
  if (contentTemplateInstance.templateType === "SHEET") {
    return [contentTemplateInstance];
  }

  if (!contentTemplateInstance.parentId) {
    return [];
  }

  // This is a drawing hosted to a content template instance
  if (doc.contentTemplateInstances[contentTemplateInstance.parentId]) {
    return docContentTemplateInstanceGetHostSheets(
      doc,
      contentTemplateInstance.parentId,
      docEntityNodeSource,
      nextVisitedInstanceIds,
    );
  }

  // This is a drawing hosted to a content template, find where the parent template is hosted
  const parentTemplate = doc.contentTemplates[contentTemplateInstance.parentId];
  if (!parentTemplate) {
    return [];
  }

  const templateNode = docEntityNodeSource.getNode(doc, { type: "CONTENT_TEMPLATE", id: parentTemplate.id });
  assertIsContentTemplateDocEntityNode(templateNode);
  return templateNode.getHostSheets(nextVisitedInstanceIds);
}

export function docContentTemplateSelectAnnotations(
  doc: Doc,
  contentTemplateId: string,
  docEntityNodeSource: DocEntityNodeSource,
  visitedInstanceIds: Set<string> = new Set(),
): Annotation[] {
  if (visitedInstanceIds.has(contentTemplateId)) {
    return [];
  }

  const templateNode = docEntityNodeSource.getNode(doc, { type: "CONTENT_TEMPLATE", id: contentTemplateId });
  if (!templateNode) {
    return [];
  }

  const nextVisitedInstanceIds = new Set(visitedInstanceIds);
  nextVisitedInstanceIds.add(contentTemplateId);

  assertIsContentTemplateDocEntityNode(templateNode);
  return templateNode.selectChildAnnotations(nextVisitedInstanceIds);
}

/**
 * DocEntityNode implementation for CONTENT_TEMPLATE entities.
 * Provides efficient reverse lookup from templates to their instances.
 */
export default class ContentTemplateDocEntityNode extends DocEntityNodeBase<"CONTENT_TEMPLATE"> {
  /**
   * Cache for the reverse index mapping template IDs to instance IDs.
   * Keyed by Doc reference to avoid rebuilding the index for each template lookup.
   */
  private static instanceIndexCache = new LRUObjectCache<Doc, Map<string, string[]>>(4, (a, b) => a === b);

  private hasModelHostedInstancesCached?: boolean;
  private hostSheets?: SheetTemplateInstance[];
  private childAnnotations?: Annotation[];

  constructor(handle: ContentTemplateHandle, doc: Doc, docEntityNodeSource: DocEntityNodeSource) {
    super(handle, doc, docEntityNodeSource);
  }

  /**
   * Get all ContentTemplateInstance entities that reference this content template.
   * Uses a cached reverse index to avoid iterating all instances repeatedly.
   */
  getContentTemplateInstances(): DocEntityNode<"CONTENT_TEMPLATE_INSTANCE">[] {
    const templateId = this.getId();
    if (!templateId) {
      return [];
    }

    const index = this.getOrBuildInstanceIndex();
    const instanceIds = index.get(templateId) ?? [];

    return instanceIds
      .map((id) => this.docEntityNodeSource.getNode(this.doc, { type: "CONTENT_TEMPLATE_INSTANCE", id }))
      .filter((node): node is DocEntityNode<"CONTENT_TEMPLATE_INSTANCE"> => !!node);
  }

  hasModelHostedInstances(): boolean {
    if (this.hasModelHostedInstancesCached !== undefined) {
      return this.hasModelHostedInstancesCached;
    }

    // If any of the instances of the drawing template are hosted in model space, then the annotation in the template is hosted in model space.
    const hasModelHostedInstances = this.getContentTemplateInstances().some((instance) =>
      docAnnotationOrDrawingIsModelSpaceHosted(this.doc, instance.getParent()?.getId(), this.docEntityNodeSource),
    );
    this.hasModelHostedInstancesCached = hasModelHostedInstances;
    return hasModelHostedInstances;
  }

  getHostSheets(visitedInstanceIds: Set<string> = new Set()): SheetTemplateInstance[] {
    if (this.hostSheets !== undefined) {
      return this.hostSheets;
    }

    const hostSheets = this.getContentTemplateInstances().flatMap((instance) =>
      docContentTemplateInstanceGetHostSheets(this.doc, instance.getId(), this.docEntityNodeSource, visitedInstanceIds),
    );
    this.hostSheets = hostSheets;
    return hostSheets;
  }

  selectChildAnnotations(visitedInstanceIds: Set<string> = new Set()): Annotation[] {
    if (this.childAnnotations) {
      return this.childAnnotations;
    }

    const children = this.getChildren();
    const directAnnotations = children
      .filter((child): child is DocEntityNode<"ANNOTATION"> => child.getEntityType() === "ANNOTATION")
      .map((node) => node.getDocEntity());

    const contentTemplateInstances = children.filter(
      (child): child is DocEntityNode<"CONTENT_TEMPLATE_INSTANCE"> =>
        child.getEntityType() === "CONTENT_TEMPLATE_INSTANCE",
    );

    const childContentAnnotations = contentTemplateInstances.flatMap((contentTemplateInstance) =>
      docContentTemplateSelectAnnotations(
        this.doc,
        contentTemplateInstance.getDocEntity().contentTemplateId,
        this.docEntityNodeSource,
        visitedInstanceIds,
      ),
    );

    const childAnnotations = directAnnotations.concat(childContentAnnotations);

    this.childAnnotations = childAnnotations;

    return childAnnotations;
  }

  /**
   * Builds or retrieves the cached reverse index mapping template IDs to instance IDs.
   * It's efficient the build the index just once per Doc and then reuse it.
   */
  private getOrBuildInstanceIndex(): Map<string, string[]> {
    let index = ContentTemplateDocEntityNode.instanceIndexCache.get(this.doc);
    if (index) {
      return index;
    }

    index = new Map<string, string[]>();

    for (const instanceId in this.doc.contentTemplateInstances) {
      const instance = this.doc.contentTemplateInstances[instanceId];
      if (!instance) {
        continue;
      }

      const { contentTemplateId } = instance;
      if (!contentTemplateId) {
        continue;
      }

      const existing = index.get(contentTemplateId) ?? [];
      existing.push(instanceId);
      index.set(contentTemplateId, existing);
    }

    ContentTemplateDocEntityNode.instanceIndexCache.add(this.doc, index);
    return index;
  }
}

export function isContentTemplateDocEntityNode(node: DocEntityNode): node is ContentTemplateDocEntityNode {
  return node instanceof ContentTemplateDocEntityNode;
}

export function assertIsContentTemplateDocEntityNode(
  node: DocEntityNode | undefined,
): asserts node is ContentTemplateDocEntityNode {
  assertIsNotUndefined(node);
  if (!isContentTemplateDocEntityNode(node)) {
    throw new Error("Node is not a ContentTemplateDocEntityNode");
  }
}
